import { computed } from 'vue'
import createClient, { type ClientOptions } from 'openapi-fetch'
import { getAppConfig } from '@/config'
import { useEnvironment } from '@/composables/useEnvironment'
import { useAuthStore } from '@/store/auth'
import type { ServiceType } from '@/constants/environments'

/*
 * Once `npm run generate:api-all` has been run and the three API type files exist,
 * replace `any` below with the concrete paths type from each generated module.
 *
 *   import type { paths as MainPaths } from '@/api/api-types'
 *   import type { paths as SectionsPaths } from '@/api/api-sections-types'
 *   import type { paths as LangschoolPaths } from '@/api/api-langschool-types'
 *
 * Then replace `Record<string, any>` with the appropriate paths type per service.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyPaths = Record<string, any>

function getApiUrl(service: ServiceType): string {
  const config = getAppConfig()
  switch (service) {
    case 'main':
      return config.mainApiUrl
    case 'sections':
      return config.sectionsApiUrl
    case 'langschool':
      return config.langschoolApiUrl
  }
}

const clientsCache = new Map<ServiceType, ReturnType<typeof createClient<AnyPaths>>>()

function createAuthClient(service: ServiceType) {
  const baseUrl = getApiUrl(service)
  const auth = useAuthStore()

  const client = createClient<AnyPaths>({
    baseUrl: `${baseUrl}/api/v2`,
    headers: {
      'Content-Type': 'application/json',
    },
  } as ClientOptions)

  // Attach the Bearer token via openapi-fetch middleware. (The previous approach
  // reassigned a non-existent `client.fetch`, so the Authorization header was
  // never sent → the API saw "invalid token" / 401.) The token is read per
  // request from the auth store, so it stays current after refresh.
  client.use({
    onRequest({ request }) {
      const token = auth.accessToken
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`)
      }
      return request
    },
  })

  return client
}

export function useApiClient(service: ServiceType) {
  const { currentServiceKey } = useEnvironment()

  const effectiveService = computed(() => service || (currentServiceKey.value as ServiceType))

  const client = computed(() => {
    const svc = effectiveService.value
    if (!clientsCache.has(svc)) {
      clientsCache.set(svc, createAuthClient(svc))
    }
    return clientsCache.get(svc)!
  })

  async function GET(path: string, params?: Record<string, any>) {
    return client.value.GET(path as any, params as any)
  }

  async function POST(path: string, params?: Record<string, any>) {
    return client.value.POST(path as any, params as any)
  }

  async function PUT(path: string, params?: Record<string, any>) {
    return client.value.PUT(path as any, params as any)
  }

  async function PATCH(path: string, params?: Record<string, any>) {
    return client.value.PATCH(path as any, params as any)
  }

  async function DELETE(path: string, params?: Record<string, any>) {
    return client.value.DELETE(path as any, params as any)
  }

  return { GET, POST, PUT, PATCH, DELETE, rawClient: client }
}
